import { clientMoveIn } from "../../lib/clientMoveIn";
import { IconButton as DesignIconButton, Tooltip as DesignTooltip } from "@mui/material";
import { CloseOutlined as DesignClose, } from "@mui/icons-material";
// src/components/clients/ClientList.tsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  Avatar,
  IconButton,
  Grid,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Stack,
  Tooltip,
  Paper,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Snackbar,
  Fab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Card,
  CardContent,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  ViewKanban as ViewKanbanIcon,
  AccessTime as AccessTimeIcon,
  Close as CloseIcon,
  Circle as CircleIcon,
  PersonAdd as PersonAddIcon,
  Inbox as InboxIcon,
  SwapHoriz as SwapHorizIcon,
  Unarchive as UnarchiveIcon,
  PhoneDisabled as PhoneDisabledIcon,
  Group as GroupIcon,
  Tour as TourIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import { gradients } from "../../theme/theme";
import { supabase } from "../../lib/supabase";
import { usePersistedState } from "../../hooks/usePersistedState";
import LeadsPage from "./LeadsPage";
import type { LeadClaimRow } from "../../lib/leadsWork";
import ClientRecordDialog from "./ClientRecordDialog";
import LocatorWorkloadStrip from "./LocatorWorkloadStrip";
import LeadDetailModal from "../dashboard/LeadDetailModal";
import { leadToDashboardRow } from "../../lib/leadsTimeline";
import { filterClientsForNavigationStatus } from "../../lib/appNavigationState";
import { templateService } from "../../lib/templateService";
import {
  resolveTemplateForSms,
  buildSmsUrl,
} from "../../lib/resolveTemplate";
import {
  buildLeadDisplayName,
  buildLeadLocationParts,
  formatLeadPets,
} from "../../lib/leadPreferences";
import GuestCardDialog from "../GuestCardDialog";
import type { SettingsTabKey } from "../settings/settingsTabs";
import { emailService } from "../../services/emailService";
import type { ClientInfo } from "../../services/emailService";
import { runLeadAction } from "../../services/leadConversionService";
import { leadActionErrorMessage } from "../../lib/leadRefusal";
import { clientMatchesSearch } from "./clientSearch";
import {
  deleteClientById,
  changeClientStatus,
  updateClientById,
} from "./clientMutations";
import {
  createAttributableClient,
  ManualIntakeNoClientError,
  ManualIntakeOrphanError,
} from "./manualClientIntake";

// Import sub-components
import ClientCard from "./ClientCard";
import PipelineView from "./PipelineView";
import NeedsTouchView from "./NeedsTouchView";
import { useMarkInvoicePaidPrompt } from "./MarkInvoicePaidDialog";
import { INVOICED_STATUSES } from "../../lib/moneyTruth";
import DetailDrawer from "./DetailDrawer";
import { fetchSmsUnreachableClientIds } from "../../services/smsAdminService";
import LogActivityDialog from "./LogActivityDialog";
import SendInvoiceModal from "./SendInvoiceModal";
import UploadLeaseModal from "./UploadLeaseModal";
import PageHeaderTitle from "../PageHeaderTitle";
import PageRefreshButton from "../PageRefreshButton";
import PageLoadingScaffold, { LoadingTopBar } from "../common/PageLoadingScaffold";

// Import types and config
import {
  CLIENT_NAVIGATION_STATUS_VALUES,
  type ClientNavigationStatus,
  type Client,
  type ClientStatus,
  type ClientType,
  type ClientPriority,
  type Lead,
  type Agent,
  type MainTab,
  type ViewMode,
} from "./types";
import {
  statusConfig,
  pipelineStatuses,
  priorityConfig,
  typeConfig,
} from "./config";
import { formatRelativeTime, formatPhoneNumber } from "./helpers";

// Small uppercase group label used inside the modernized Convert dialog.
const sectionLabelSx = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: ".08em",
  textTransform: "uppercase",
  color: "text.secondary",
  mb: 1,
} as const;

interface ClientListProps {
  initialSocialSource?: string | null;
  onSocialSourceConsumed?: () => void;
  onNavigate?: (
    page: string,
    clientId?: string,
    params?: { settingsTab?: SettingsTabKey }
  ) => void;
  initialSelectedClientId?: string | null;
  initialStatusFilter?: string | null;
  initialDrawerTab?: string | null;
  initialMainTab?: MainTab | null;
  onClientDrawerOpen?: (clientId: string, tab?: string) => void;
  onClientDrawerTabChange?: (tab: string) => void;
  onClientDrawerClose?: () => void;
  isDemo?: boolean;
}
const MAIN_TABS = new Set<MainTab>(["leads", "active", "out_of_contact"]);
const VIEW_MODES = new Set<ViewMode>(["grid", "list", "pipeline", "needs_touch"]);
const CLIENT_FILTER_STATUSES = new Set<string>([...CLIENT_NAVIGATION_STATUS_VALUES, "all"]);
const isMainTab = (value: unknown): value is MainTab => MAIN_TABS.has(value as MainTab);
const isViewMode = (value: unknown): value is ViewMode => VIEW_MODES.has(value as ViewMode);
const isClientFilterStatus = (value: unknown): value is ClientNavigationStatus | "all" =>
  typeof value === "string" && CLIENT_FILTER_STATUSES.has(value);
const isStringPreference = (value: unknown): value is string => typeof value === "string";
const isMoveInWindow = (value: unknown): value is "all" | "30" | "60" | "90" =>
  value === "all" || value === "30" || value === "60" || value === "90";

const ClientList: React.FC<ClientListProps> = ({
  onNavigate,
  initialSelectedClientId, initialSocialSource, onSocialSourceConsumed,
  initialStatusFilter,
  initialDrawerTab,
  initialMainTab,
  onClientDrawerOpen,
  onClientDrawerTabChange,
  onClientDrawerClose,
  isDemo = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isLocalDemoHost =
    isDemo &&
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  // Main tab state
  // Ch78 · Grant (2026-09-10): "make incoming leads the landing tab for the
  // Leads page." The top of the funnel is what goes stale fastest.
  //
  // NOTE the default is declared TWICE — here and in persistenceRegistry. The
  // hook reads THIS one; the registry entry is the documented contract. They
  // must agree, and persistenceSourceInventory is what notices when they stop.
  const [mainTab, setMainTab] = usePersistedState<MainTab>(
    "clients.mainTab",
    "leads",
    { validate: isMainTab }
  );
  const [viewMode, setViewMode] = usePersistedState<ViewMode>(
    "clients.viewMode",
    "grid",
    { validate: isViewMode }
  );
  // Ch78 · the claim ledger (migration 049), read for the Leads page's stages.
  // null means the read FAILED — the page says so rather than drawing every
  // claimed lead as unclaimed.
  const [leadClaims, setLeadClaims] = useState<LeadClaimRow[] | null>([]);
  // Ch78 · a lead opened from the Move-in Board (or its gutters).
  const [timelineLead, setTimelineLead] = useState<Lead | null>(null);
  // Ch78 · a CLIENT opened from the board — the complete record modal.
  const [boardClientId, setBoardClientId] = useState<string | null>(null);

  // Data states
  const [clients, setClients] = useState<Client[]>([]);
  const clientLoadEpoch = useRef(0);
  const clientLoadMounted = useRef(false);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [currentUser, setCurrentUser] = useState<{
    id: string;
    role: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [leadsLoading, setLeadsLoading] = useState(true);

  // Filter states
  const [searchQuery, setSearchQuery] = usePersistedState<string>(
    "clients.searchQuery",
    "",
    { validate: isStringPreference }
  );
  const [statusFilter, setStatusFilter] = usePersistedState<ClientNavigationStatus | "all">(
    "clients.statusFilter",
    isClientFilterStatus(initialStatusFilter) ? initialStatusFilter : "all",
    { validate: isClientFilterStatus }
  );
  // undefined = loading, null = failed coverage; only a Set is a known result.
  const [invoiceSentClientIds, setInvoiceSentClientIds] = useState<Set<string> | null | undefined>(undefined);
  // PKT-0916 (B) · ids of clients with a sent/overdue/paid invoice, for the
  // "Send invoice" nudge. undefined = still loading, null = the read FAILED
  // (the view says so rather than listing everyone as un-invoiced).
  const [invoicedClientIds, setInvoicedClientIds] = useState<Set<string> | null | undefined>(undefined);
  // Client reloads follow confirmed invoice actions and the Refresh button.
  // Re-read membership too: an unchanged view must not keep an old Send nudge.
  useEffect(() => {
    if (viewMode !== "needs_touch") return;
    let cancelled = false;
    setInvoicedClientIds(undefined);
    void Promise.resolve(
      supabase.from("invoices").select("client_id").in("status", [...INVOICED_STATUSES])
    )
      .then(({ data, error }: { data: any[] | null; error: unknown }) => {
        if (cancelled) return;
        if (error) {
          console.error("needs-touch: could not read invoices", error);
          setInvoicedClientIds(null);
          return;
        }
        setInvoicedClientIds(new Set((data ?? []).map((row: any) => row.client_id).filter(Boolean)));
      })
      .catch((err: unknown) => {
        console.error("needs-touch: could not read invoices", err);
        if (!cancelled) setInvoicedClientIds(null);
      });
    return () => { cancelled = true; };
  }, [viewMode, clients]);
  useEffect(() => {
    if (statusFilter !== "invoice_sent") return;
    let cancelled = false;
    setInvoiceSentClientIds(undefined);
    void Promise.resolve(supabase.from("invoices").select("client_id").in("status", ["sent", "overdue"]))
      .then(({ data, error }) => {
        if (cancelled) return;
        setInvoiceSentClientIds(error ? null : new Set((data ?? []).map((row: any) => row.client_id).filter(Boolean)));
      })
      .catch(() => {
        if (!cancelled) setInvoiceSentClientIds(null);
      });
    return () => { cancelled = true; };
  }, [statusFilter, clients]);

  // Sync filter when navigation passes a new initial status
  useEffect(() => {
    if (initialStatusFilter) {
      if (isClientFilterStatus(initialStatusFilter)) setStatusFilter(initialStatusFilter);
    }
  }, [initialStatusFilter]);
  useEffect(() => {
    if (initialMainTab) {
      setMainTab(initialMainTab);
    }
  }, [initialMainTab]);
  const [agentFilter, setAgentFilter] = useState<string>("all");
  // Move-in date window filter (Davis 2026-04-25 — surface time-pressure
  // clients quickly). "all" = no filter; numeric = keep clients with
  // Preferred move-in date within now + N days. Persisted across nav.
  const [moveInWindow, setMoveInWindow] = usePersistedState<
    "all" | "30" | "60" | "90"
  >("clients.moveInWindow", "all", { validate: isMoveInWindow });
  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [detailClient, setDetailClient] = useState<Client | null>(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  // TRAIN-4: ids of clients with a STOP on record. Best-effort — agents get a
  // 403 from the admin endpoint and simply see no badges (D9-consistent).
  const [smsUnreachableIds, setSmsUnreachableIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    fetchSmsUnreachableClientIds()
      .then((ids) => setSmsUnreachableIds(new Set(ids)))
      .catch(() => setSmsUnreachableIds(new Set()));
  }, []);
  const [drawerInitialTab, setDrawerInitialTab] = useState<string | null>(null);
  const [drawerOpenAction, setDrawerOpenAction] = useState<
    "addNote" | "addTask" | null
  >(null);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertingLead, setConvertingLead] = useState<Lead | null>(null);

  // Call Dialog state (desktop "Log Call" popup)
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [callDialogClient, setCallDialogClient] = useState<Client | null>(null);
  const [callPhoneCopied, setCallPhoneCopied] = useState(false);

  // Log Activity Dialog state
  const [logActivityDialogOpen, setLogActivityDialogOpen] = useState(false);
  const [logActivityClient, setLogActivityClient] = useState<Client | null>(
    null
  );
  const [logActivityInitialType, setLogActivityInitialType] = useState<
    string | undefined
  >(undefined);
  // PKT-0410: launch provenance for the mobile Call/Text handoff, carried until
  // the operator confirms in LogActivityDialog. Cleared on every close so a
  // later manual log cannot inherit a previous handoff's launch_mode.
  const [logActivityExtraMetadata, setLogActivityExtraMetadata] = useState<
    Record<string, unknown> | undefined
  >(undefined);

  // Guest Card Dialog state
  const [guestCardDialogOpen, setGuestCardDialogOpen] = useState(false);
  const [guestCardClient, setGuestCardClient] = useState<Client | null>(null);

  // Send Invoice Modal state
  const [sendInvoiceDialogOpen, setSendInvoiceDialogOpen] = useState(false);
  const [demoInvoiceDialogOpen, setDemoInvoiceDialogOpen] = useState(false);
  const [sendInvoiceClient, setSendInvoiceClient] = useState<Client | null>(
    null
  );

  // Upload Lease Modal state
  const [uploadLeaseDialogOpen, setUploadLeaseDialogOpen] = useState(false);
  const [uploadLeaseClient, setUploadLeaseClient] = useState<Client | null>(
    null
  );

  // Form states
  const [formData, setFormData] = useState<Partial<Client>>({});
  const [convertFormData, setConvertFormData] = useState<Partial<Client>>({});

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    // "warning" carries the P2-A orphan case: the client was not created but the
    // lead WAS saved, so it is neither a success nor a plain failure. Rendering
    // it red would contradict a message that says nothing was lost.
    severity: "success" | "error" | "warning";
  }>({ open: false, message: "", severity: "success" });
  const isElevatedRole = (role?: string | null) =>
    role === "admin" || role === "manager";

  const canManageAssignments = isElevatedRole(currentUser?.role);
  const isAgentRole = (currentUser?.role || "").toLowerCase() === "agent";
  // Ch78 · Grant's rebuilt Leads page is on screen. It states the IA rule in
  // one header line and carries no locator strip ("'Who's working them' can be
  // removed"); every remaining explanation is an (i) hover inside a card.
  // Grant deleted the page this replaced on 2026-09-10 — there is no "old" to
  // fall back to any more, which is the point: one Leads page, one answer.
  const showRebuiltLeadsPage = !isAgentRole && mainTab === "leads";
  useEffect(() => {
    if (isAgentRole && mainTab === "leads") {
      setMainTab("active");
    }
  }, [isAgentRole, mainTab, setMainTab]);
  const normalizeOptionalString = (value: unknown): string | null | unknown => {
    if (typeof value !== "string") return value;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  };
  const normalizeOptionalNumber = (value: unknown): number | null | unknown => {
    if (value === "" || value === null || value === undefined) return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  };
  const normalizeDateInputValue = (
    value: string | null | undefined
  ): string => {
    if (!value) return "";
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) return "";
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  const buildWritableClientPayload = (
    source: Record<string, any>,
    userId: string
  ) => {
    const payload: Record<string, any> = {
      ...source,
      first_name: String(source.first_name ?? "").trim(),
      last_name: String(source.last_name ?? "").trim(),
      email: String(source.email ?? "")
        .trim()
        .toLowerCase(),
      status: source.status || "new_client",
      client_type: source.client_type || "renter",
      priority: source.priority || "normal",
      phone: normalizeOptionalString(source.phone),
      notes: normalizeOptionalString(source.notes),
      lead_source: normalizeOptionalString(source.lead_source),
      move_in_timeline: normalizeOptionalString(source.move_in_timeline),
      ...(source.move_in_date !== undefined ? { move_in_date: normalizeOptionalString(source.move_in_date) } : {}),
      employer: normalizeOptionalString(source.employer),
      income_range: normalizeOptionalString(source.income_range),
      max_budget: normalizeOptionalNumber(source.max_budget),
      min_bedrooms: normalizeOptionalNumber(source.min_bedrooms),
      preferred_areas: Array.isArray(source.preferred_areas)
        ? source.preferred_areas
            .map((area: unknown) => String(area ?? "").trim())
            .filter(Boolean)
        : source.preferred_areas,
    };

    if (payload.assigned_agent_id === "") {
      payload.assigned_agent_id = null;
    }
    if (!canManageAssignments) {
      payload.assigned_agent_id = userId;
    }

    return payload;
  };
  const getClientSaveErrorMessage = (error: unknown): string => {
    // EVENT-2179 · the branches below test `error.code`, a POSTGRES code that only
    // ever appears on a Supabase error. An `ApiError` has `status` and `body` and
    // NO `code` (lib/api.ts:96-107), so every branch missed it and an HTTP refusal
    // fell to the `typed.message` rung — printing "Failed to save client: API
    // error: 409": a status code wearing a save label, for a refusal that was not
    // a save at all. The two callers that delivered those ApiErrors were the lead
    // CLAIM handlers, and they now route to `leadActionErrorMessage` instead.
    //
    // No ApiError rung was added here. Every remaining await under this catch goes
    // through Supabase (`updateClientById`, the `leads` insert), and the one HTTP call in the path —
    // `runLeadAction` via `convertLead` — is wrapped as `ManualIntakeOrphanError`
    // at manualClientIntake.ts:163 and handled by the `isOrphan` branch before it
    // gets here. So an `ApiError` has no reachable route to this function today: a
    // rung for it could not be driven red, and an unexercised guard is worse than
    // an honest absence. Recorded in the EVENT-2179 handoff instead.
    const typed = error as {
      code?: string;
      message?: string;
      details?: string;
    };
    if (
      typed?.code === "23505" &&
      typeof typed.message === "string" &&
      typed.message.includes("clients_email_key")
    ) {
      return "A client with this email already exists";
    }
    if (typed?.code === "42501") {
      return "You do not have permission to save this client";
    }
    if (typeof typed?.message === "string" && typed.message.length > 0) {
      return `Failed to save client: ${typed.message}`;
    }
    return "Failed to save client";
  };

  useEffect(() => {
    clientLoadMounted.current = true;
    let cancelled = false;
    const bootstrapData = async () => {
      const viewer = await loadCurrentUser();
      if (cancelled) return;
      await Promise.all([
        loadAgents(viewer),
        loadClients(viewer),
        loadLeads(viewer),
      ]);
    };
    void bootstrapData();
    return () => {
      cancelled = true;
      clientLoadMounted.current = false;
      clientLoadEpoch.current += 1;
    };
  }, []);

  useEffect(() => {
    if (currentUser && !isElevatedRole(currentUser.role)) {
      setAgentFilter(currentUser.id);
    }
  }, [currentUser]);

  // Keep open drawer in sync with the latest clients list
  useEffect(() => {
    if (!detailClient) return;
    const fresh = clients.find((c) => c.id === detailClient.id);
    if (fresh && fresh !== detailClient) {
      setDetailClient(fresh);
    }
  }, [clients, detailClient]);

  const previousSelectedClientIdRef = useRef(initialSelectedClientId);

  // The URL owns drawer presence. A cold load or Forward opens it; Back to an
  // entry without `client` closes it. Opening from the list updates the URL in
  // the same event, so there is never a handled-and-erased deep link.
  useEffect(() => {
    if (initialSelectedClientId && !loading) {
      const client = clients.find((c) => c.id === initialSelectedClientId);
      if (client) {
        setDetailClient(client);
        setDetailDrawerOpen(true);
        // Switch to appropriate tab based on client status
        if (client.status === "out_of_contact") {
          setMainTab("out_of_contact");
        } else {
          setMainTab("active");
        }
      } else {
        // A stale/deleted/reassigned URL must never leave the previously open
        // client's record visible under the requested ID.
        setDetailClient(null);
        setDetailDrawerOpen(false);
        setDrawerInitialTab(null);
        setDrawerOpenAction(null);
      }
    } else if (
      !initialSelectedClientId &&
      previousSelectedClientIdRef.current &&
      detailDrawerOpen
    ) {
      setDetailDrawerOpen(false);
      setDrawerInitialTab(null);
      setDrawerOpenAction(null);
    }
    previousSelectedClientIdRef.current = initialSelectedClientId;
  }, [initialSelectedClientId, clients, loading, detailDrawerOpen, setMainTab]);

  const refreshingClients = loading || leadsLoading;
  const clientsInitialLoading = loading && clients.length === 0;
  const leadsInitialLoading = leadsLoading && leads.length === 0;
  const urlOwnedDetailClient =
    detailClient?.id === initialSelectedClientId ? detailClient : null;

  const loadCurrentUser = async (): Promise<{
    id: string;
    role: string;
  } | null> => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("role")
          .eq("id", user.id)
          .single();
        const viewer = { id: user.id, role: profile?.role || "agent" };
        setCurrentUser(viewer);
        return viewer;
      }
    } catch (err) {
      console.error("Error loading current user:", err);
    }
    return null;
  };

  const loadAgents = async (
    viewer?: { id: string; role: string } | null
  ) => {
    try {
      const effectiveViewer = viewer ?? currentUser;
      let query = supabase
        .from("profiles")
        .select("id, full_name, email, avatar_url, role");
      if (effectiveViewer && !isElevatedRole(effectiveViewer.role)) {
        query = query.eq("id", effectiveViewer.id);
      }

      const { data, error } = await query.order("full_name");

      if (error) {
        console.error("Error loading agents:", error);
        throw error;
      }

      setAgents(data || []);
    } catch (err) {
      console.error("Error loading agents:", err);
      // Try without role column in case it doesn't exist
      try {
        const effectiveViewer = viewer ?? currentUser;
        let query = supabase
          .from("profiles")
          .select("id, full_name, email, avatar_url");
        if (effectiveViewer && !isElevatedRole(effectiveViewer.role)) {
          query = query.eq("id", effectiveViewer.id);
        }

        const { data, error } = await query.order("full_name");
        if (!error && data) {
          setAgents(data.map((p) => ({ ...p, role: "agent" })));
        }
      } catch (fallbackErr) {
        console.error("Fallback also failed:", fallbackErr);
      }
    }
  };

  const loadClients = async (
    viewer?: { id: string; role: string } | null
  ) => {
    if (!clientLoadMounted.current) return;
    const epoch = ++clientLoadEpoch.current;
    const isCurrent = () => clientLoadMounted.current && epoch === clientLoadEpoch.current;
    try {
      setLoading(true);
      const effectiveViewer = viewer ?? currentUser;
      let query = supabase.from("clients").select("*");
      if (effectiveViewer && !isElevatedRole(effectiveViewer.role)) {
        query = query.eq("assigned_agent_id", effectiveViewer.id);
      }

      const { data, error } = await query.order("created_at", {
        ascending: false,
      });

      if (!isCurrent()) return;
      if (error) throw error;

      const clientsData = data || [];
      const assignedAgentIds = Array.from(
        new Set(
          clientsData
            .map((client) => client.assigned_agent_id)
            .filter((id): id is string => Boolean(id))
        )
      );

      const agentsById = new Map<string, Agent>();
      if (assignedAgentIds.length > 0) {
        const { data: assignedAgents, error: agentsError } = await supabase
          .from("profiles")
          .select("id, full_name, email, avatar_url, role")
          .in("id", assignedAgentIds);

        if (!isCurrent()) return;
        if (agentsError) {
          console.error("Error loading assigned agents:", agentsError);
        } else {
          (assignedAgents || []).forEach((agent) => {
            agentsById.set(agent.id, agent as Agent);
          });
        }
      }

      const clientsWithAgents = clientsData.map((client) => ({
        ...client,
        assigned_agent: client.assigned_agent_id
          ? agentsById.get(client.assigned_agent_id)
          : undefined,
      }));

      setClients(clientsWithAgents);
    } catch (error) {
      if (!isCurrent()) return;
      console.error("Error loading clients:", error);
      setSnackbar({
        open: true,
        message: "Failed to load clients",
        severity: "error",
      });
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  const loadLeads = async (
    viewer?: { id: string; role: string } | null
  ) => {
    try {
      setLeadsLoading(true);
      const effectiveViewer = viewer ?? currentUser;
      if (effectiveViewer && !isElevatedRole(effectiveViewer.role)) {
        setLeads([]);
        return;
      }

      const { data, error } = await supabase
        .from("leads")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setLeads(data || []);

      // Ch78 · the Leads page needs claim state to tell "nobody has picked
      // this up" from "somebody has, and has not called yet". A failure here
      // is reported, never silently rendered as "unclaimed".
      const claims = await supabase
        .from("lead_claims")
        .select("lead_id, owner_user_id, claimed_at, converted_client_id");
      if (claims.error) {
        console.error("Error loading lead claims:", claims.error);
        setLeadClaims(null);
      } else {
        setLeadClaims((claims.data as LeadClaimRow[]) || []);
      }
    } catch (error) {
      console.error("Error loading leads:", error);
    } finally {
      setLeadsLoading(false);
    }
  };

  // Filtered data
  const activeClients = useMemo(() => {
    return clients.filter((c) => c.status !== "out_of_contact");
  }, [clients]);

  const outOfContactClients = useMemo(() => {
    return clients.filter((c) => c.status === "out_of_contact");
  }, [clients]);

  const statusOrder: Record<string, number> = {
    new_client: 0,
    touring: 1,
    applied: 2,
    payment_received: 3,
    closed: 4,
  };

  const filteredActiveClients = useMemo(() => {
    let result = [...activeClients];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter((c) => clientMatchesSearch(c, q));
    }
    result = filterClientsForNavigationStatus(result, statusFilter, invoiceSentClientIds ?? new Set());
    if (agentFilter !== "all")
      result = result.filter((c) => c.assigned_agent_id === agentFilter);
    if (moveInWindow !== "all") {
      // Clients with no move-in date are excluded from the time-pressure
      // view — date-pressure filter shouldn't surface no-date clients.
      // Past dates are kept (overdue counts as urgent).
      const days = parseInt(moveInWindow, 10);
      const cutoffMs = Date.now() + days * 24 * 60 * 60 * 1000;
      result = result.filter((c) => {
        const raw = clientMoveIn(c);
        if (!raw) return false;
        const t = Date.parse(raw);
        return !isNaN(t) && t <= cutoffMs;
      });
    }
    result.sort((a, b) => (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99));
    return result;
  }, [activeClients, searchQuery, statusFilter, agentFilter, moveInWindow, invoiceSentClientIds]);

  const filteredOutOfContactClients = useMemo(() => {
    let result = [...outOfContactClients];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter((c) => clientMatchesSearch(c, q));
    }
    return result;
  }, [outOfContactClients, searchQuery]);

  const newLeadsCount = useMemo(() => {
    return leads.filter((l) => !l.status || l.status === "new").length;
  }, [leads]);

  // F24 (migration 122) · test clients stay in the lists, never in the numbers.
  const countedActive = useMemo(() => activeClients.filter((c) => c.is_test_client !== true), [activeClients]);
  const countedOutOfContact = useMemo(() => outOfContactClients.filter((c) => c.is_test_client !== true), [outOfContactClients]);
  const stats = useMemo(
    () => ({
      total: countedActive.length,
      newClient: countedActive.filter((c) => c.status === "new_client").length,
      touring: countedActive.filter((c) => c.status === "touring").length,
      applied: countedActive.filter((c) => c.status === "applied").length,
      paymentReceived: countedActive.filter(
        (c) => c.status === "payment_received"
      ).length,
      outOfContact: countedOutOfContact.length,
    }),
    [countedActive, countedOutOfContact]
  );

  // Handlers
  const handleEditClient = (client: Client) => {
    setEditingClient(client);
    // Keep the stored timeline intact; the date control reads the canonical fallback.
    setFormData({ ...client });
    setEditDialogOpen(true);
  };

  const handleNewClient = () => {
    if (isDemo) return;
    setEditingClient(null);
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      status: "new_client",
      client_type: "renter",
      priority: "normal",
      assigned_agent_id: currentUser?.id,
    });
    setEditDialogOpen(true);
  };

  const consumedSocialSource = useRef<string | null>(null);
  useEffect(() => {
    if (!initialSocialSource || isDemo || !currentUser || consumedSocialSource.current === initialSocialSource) return;
    consumedSocialSource.current = initialSocialSource;
    onSocialSourceConsumed?.();
    setEditingClient(null);
    setFormData({first_name:"", last_name:"", email:"", status:"new_client", client_type:"renter", priority:"normal",
      assigned_agent_id:currentUser.id, lead_source:initialSocialSource, notes:`Source post: ${initialSocialSource}`});
    setEditDialogOpen(true);
  }, [initialSocialSource, isDemo, currentUser, onSocialSourceConsumed]);

  const handleSaveClient = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      // Strip joined/computed fields that aren't actual columns
      const {
        id: _id,
        created_at: _created,
        updated_at: _updated,
        assigned_agent: _agent,
        user_id: _userId,
        created_by: _createdBy,
        ...writable
      } = formData as Record<string, any>;
      if (
        editingClient?.move_in_timeline &&
        !normalizeDateInputValue(editingClient.move_in_timeline) &&
        !writable.move_in_timeline
      ) {
        writable.move_in_timeline = editingClient.move_in_timeline;
      }
      const writableWithRoleScope = buildWritableClientPayload(writable, user.id);

      if (
        !writableWithRoleScope.first_name ||
        !writableWithRoleScope.last_name ||
        !writableWithRoleScope.email
      ) {
        setSnackbar({
          open: true,
          message: "First name, last name, and email are required",
          severity: "error",
        });
        return;
      }

      if (editingClient) {
        const { error } = await updateClientById(
          supabase,
          editingClient.id,
          writableWithRoleScope
        );
        if (error) throw error;
        setSnackbar({
          open: true,
          message: "Client updated",
          severity: "success",
        });
      } else {
        // P2-A · a manually-created client must ARRIVE as a lead, so revenue
        // attribution can see it. The direct `clients` INSERT that used to live
        // here produced no lead row and therefore no
        // `lead_acquisition_snapshots` row (its only writer is the AFTER INSERT
        // trigger on `leads`) and no `origin_lead_id` (its only writer is the
        // claim/convert RPC) — which made every hand-entered client invisible to
        // the funnel view PERMANENTLY, not just until the next report.
        // See manualClientIntake.ts for the full rationale and the orphan
        // disposition.
        const { clientId } = await createAttributableClient(
          {
            insertLead: async (row) => {
              const { data, error: leadError } = await supabase
                .from("leads")
                .insert(row)
                .select("id")
                .single();
              if (leadError) throw leadError;
              if (!data?.id) throw new Error("Lead insert returned no row");
              return { id: data.id as string };
            },
            convertLead: (leadId) => runLeadAction(leadId, "convert"),
            // Routed through the scoped helper like every other mutation in this
            // file. #343 landed this as a raw `.from("clients").update().eq()`
            // while #346 was still open; on rebase the source contract in
            // clientMutations.test.ts flagged it, which is exactly what that
            // contract exists to do. The filter now lives inside the helper, so
            // this call site cannot express an unscoped update.
            updateClient: async (id, formPayload) => {
              const { error: updateError } = await updateClientById(
                supabase,
                id,
                formPayload
              );
              if (updateError) throw updateError;
            },
          },
          writableWithRoleScope
        );
        setSnackbar({
          open: true,
          message: "Client created",
          severity: "success",
        });
      }
      setEditDialogOpen(false);
      loadClients();
    } catch (error: unknown) {
      console.error("Error saving client:", error);
      // P2-A · the orphan case is SURFACED, never folded into the generic save
      // error. The lead was saved and is sitting in the New Leads feed; telling
      // the operator "couldn't save client" would leave them believing nothing
      // happened while a real lead waits for them. The lead is deliberately not
      // deleted — see ManualIntakeOrphanError.
      const isOrphan =
        error instanceof ManualIntakeOrphanError ||
        error instanceof ManualIntakeNoClientError;
      setSnackbar({
        open: true,
        message: isOrphan
          ? (error as Error).message
          : getClientSaveErrorMessage(error),
        severity: isOrphan ? "warning" : "error",
      });
    }
  };

  const handleDeleteClient = async (id: string) => {
    if (isDemo) return;
    if (!window.confirm("Delete this client?")) return;
    try {
      await deleteClientById(supabase, id);
      loadClients();
      setSnackbar({
        open: true,
        message: "Client deleted",
        severity: "success",
      });
    } catch (error) {
      console.error("Error deleting client:", error);
      setSnackbar({
        open: true,
        message: "Failed to delete",
        severity: "error",
      });
    }
  };

  const handleViewDetails = (client: Client) => {
    setDetailClient(client);
    setDrawerInitialTab("timeline");
    setDetailDrawerOpen(true);
    onClientDrawerOpen?.(client.id, "timeline");
  };

  const handleDrawerTabChange = (tab: string) => {
    // Once the operator chooses a tab, the URL becomes authoritative. Keeping
    // the one-shot local launch hint here would mask Back/Forward tab changes.
    setDrawerInitialTab(null);
    onClientDrawerTabChange?.(tab);
  };

  // PKT-0916 (A, reverse): after a move INTO Payment Received, offer to mark
  // the client's open invoice paid (same backend route as the invoice modal).
  const markPaid = useMarkInvoicePaidPrompt(() => {
    void loadClients();
  });

  const handleStatusChange = async (
    client: Client,
    newStatus: ClientStatus
  ) => {
    const previousStatus = client.status;

    // Optimistic update — immediately reflect new status in the list
    setClients((prev) =>
      prev.map((c) => (c.id === client.id ? { ...c, status: newStatus } : c))
    );

    try {
      if (isLocalDemoHost) {
        setSnackbar({
          open: true,
          message: `Status changed to ${statusConfig[newStatus].label}`,
          severity: "success",
        });
        return;
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      // ONE writer, shared with the Messages rail — see changeClientStatus.
      // This block used to inline the UPDATE + activity + notification, and the
      // rail wrote only the UPDATE; the same move produced different history
      // depending on which screen fired it. Labels now come from statusConfig
      // rather than a second hand-maintained map that could drift from it.
      const outcome = await changeClientStatus(supabase, {
        clientId: client.id,
        clientName: `${client.first_name} ${client.last_name}`,
        previousStatus: previousStatus,
        nextStatus: newStatus,
        labelFor: (st) => statusConfig[st as ClientStatus]?.label || st,
        actorUserId: user.id,
      });
      if (outcome.error) throw outcome.error;
      if (!outcome.sideEffectsWritten) {
        // Name the move. A bare string says SOMETHING failed and nothing about
        // which one — and this now fires for real (it could not before), so it
        // needs to be diagnosable rather than merely present.
        console.error("status change: activity/notification insert failed", {
          clientId: client.id,
          from: previousStatus,
          to: newStatus,
          errors: outcome.sideEffectErrors,
        });
      }

      void loadClients();
      setSnackbar({
        open: true,
        message: `Status changed to ${statusConfig[newStatus].label}`,
        severity: "success",
      });
      if (newStatus === "payment_received" && previousStatus !== "payment_received") {
        void markPaid.offer(client.id, `${client.first_name} ${client.last_name}`.trim());
      }
    } catch (error) {
      console.error("Error changing status:", error);
      // Revert optimistic update on failure
      setClients((prev) =>
        prev.map((c) => (c.id === client.id ? { ...c, status: previousStatus } : c))
      );
      setSnackbar({
        open: true,
        message: "Failed to change status",
        severity: "error",
      });
    }
  };

  /**
   * PKT-0410 — replaces logMobileContactHandoff, which wrote contact truth at
   * dialer/composer LAUNCH.
   *
   * Why that was wrong: migration 031's trg_bump_client_last_contacted_on_activity
   * fires AFTER INSERT ON activities and sets clients.last_contacted_at FROM THE
   * ROW — it never reads metadata, so `connection_confirmed: false` and
   * `send_confirmed: false` exempted nothing. BOTH activity types the old helper
   * wrote (`call_logged` and `sent_text`) are in that trigger's list. The helper
   * ALSO wrote clients.last_contacted_at explicitly, so a mobile operator merely
   * TAPPING Call or Text advanced the staleness clock twice over — for a call
   * they may never have connected and a text they may never have sent.
   *
   * Desktop never had this bug: it opens LogActivityDialog and logs only what the
   * operator confirms. Mobile now routes through that same dialog after the
   * native app is launched — `tel:`/`sms:` backgrounds the page rather than
   * unmounting it, so the dialog is waiting on return. Mobile and desktop now
   * agree about what tapping the button means.
   *
   * Honest cost: an attempt the operator never confirms leaves no record. That is
   * the same trade the estate already ruled for the shortlist path (PKT-0385
   * fix (a)) — an unconfirmed attempt is not contact.
   */
  const openMobileHandoffLog = (
    client: Client,
    activityType: "call_logged" | "sent_text",
    launchMetadata: Record<string, unknown>,
  ) => {
    setLogActivityClient(client);
    setLogActivityInitialType(activityType);
    setLogActivityExtraMetadata(launchMetadata);
    setLogActivityDialogOpen(true);
  };

  const handleQuickAction = async (action: string, client: Client) => {
    switch (action) {
      case "call": {
        const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );
        if (isMobile && client.phone) {
          // Launch the dialer, then leave the log dialog waiting for the
          // operator's return. Nothing is written until they confirm.
          openMobileHandoffLog(client, "call_logged", {
            launch_mode: "mobile_tel_handoff",
          });
          window.location.href = `tel:${client.phone.replace(/[^\d+]/g, "")}`;
          break;
        } else {
          // Desktop: show formatted-number dialog with Call/Copy + log modal
          setCallDialogClient(client);
          setCallDialogOpen(true);
        }
        break;
      }
      case "email":
      case "emailed":
        window.open(`mailto:${client.email}`);
        setLogActivityClient(client);
        setLogActivityInitialType("email_sent");
        setLogActivityDialogOpen(true);
        break;
      case "sent_text": {
        // On mobile, open the native SMS app with the user's default
        // "follow_up" SMS template pre-filled when one exists. Desktop
        // still falls through to the activity log modal only; mobile
        // falls back to a bare sms: open when no default template is
        // saved or the lookup fails, so the button always produces a
        // working compose screen.
        const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );
        if (isMobile && client.phone) {
          const phone = client.phone;
          (async () => {
            let body = "";
            try {
              const tmpl = await templateService.getDefaultTemplate(
                "sms",
                "follow_up",
              );
              if (tmpl) {
                const config = emailService.getConfig();
                const resolved = resolveTemplateForSms({
                  template: tmpl,
                  client,
                  agent: {
                    full_name: config.agentName || null,
                    email: config.agentEmail || null,
                    phone: config.agentPhone || null,
                  },
                });
                body = resolved.body;
              }
            } catch (err) {
              console.warn(
                "default SMS template lookup failed; opening bare sms:",
                err,
              );
            }
            // Launch the composer, then leave the log dialog waiting for the
            // operator's return. Nothing is written until they confirm.
            openMobileHandoffLog(client, "sent_text", {
              launch_mode: "mobile_sms_handoff",
              template_prefilled: Boolean(body),
            });
            const url =
              buildSmsUrl(phone, body) ||
              `sms:${phone.replace(/[^\d+]/g, "")}`;
            window.location.href = url;
          })();
          break;
        }
        setLogActivityClient(client);
        setLogActivityInitialType("sent_text");
        setLogActivityDialogOpen(true);
        break;
      }
      case "left_voicemail":
        setLogActivityClient(client);
        setLogActivityInitialType("left_voicemail");
        setLogActivityDialogOpen(true);
        break;
      case "spoke_on_phone":
        setLogActivityClient(client);
        setLogActivityInitialType("call_logged");
        setLogActivityDialogOpen(true);
        break;
      case "logActivity":
        setLogActivityClient(client);
        setLogActivityInitialType(undefined);
        setLogActivityDialogOpen(true);
        break;
      case "search":
      case "runSearch":
        if (onNavigate) onNavigate("search", client.id);
        else window.location.href = `/?page=search&client=${client.id}`;
        break;
      case "send_list":
        if (onNavigate) onNavigate("search", client.id);
        else window.location.href = `/?page=search&client=${client.id}`;
        break;
      case "send_guest_card":
        setGuestCardClient(client);
        setGuestCardDialogOpen(true);
        break;
      case "send_invoice":
        if (isDemo) {
          setDemoInvoiceDialogOpen(true);
        } else {
          setSendInvoiceClient(client);
          setSendInvoiceDialogOpen(true);
        }
        break;
      case "upload_lease":
        if (isDemo) {
          setDemoInvoiceDialogOpen(true);
        } else {
          setUploadLeaseClient(client);
          setUploadLeaseDialogOpen(true);
        }
        break;
      case "addNote":
        setDetailClient(client);
        setDrawerInitialTab("details");
        setDrawerOpenAction("addNote");
        setDetailDrawerOpen(true);
        onClientDrawerOpen?.(client.id, "timeline");
        break;
      case "addTask":
        setDetailClient(client);
        setDrawerInitialTab("activity");
        setDrawerOpenAction("addTask");
        setDetailDrawerOpen(true);
        onClientDrawerOpen?.(client.id, "timeline");
        break;
      case "markOutOfContact":
        handleStatusChange(client, "out_of_contact");
        break;
      case "restoreClient":
        handleStatusChange(client, "new_client");
        break;
      default:
        break;
    }
  };

  const handleActivityLogSuccess = () => {
    loadClients();
    setSnackbar({
      open: true,
      message: "Activity logged successfully",
      severity: "success",
    });
  };

  const handleInvoiceSuccess = () => {
    loadClients();
    setSnackbar({
      open: true,
      message: "Invoice sent successfully!",
      severity: "success",
    });
  };

  // Lead handlers
  const handleConvertLead = async (lead: Lead) => {
    const displayName = buildLeadDisplayName(lead);
    const nameParts = displayName === "Unknown" ? [""] : displayName.split(/\s+/);
    const firstName = nameParts[0] || "";
    const budget =
      lead.budget_number ||
      (lead.budget ? parseInt(lead.budget.replace(/[^0-9]/g, "")) : undefined);
    const locationParts = buildLeadLocationParts(
      lead.neighborhoods,
      lead.neighborhood_other,
    );
    const pets = formatLeadPets(lead.pets);

    let minBedrooms: number | undefined;
    if (lead.apartment_size) {
      if (lead.apartment_size.toUpperCase().includes("STUDIO")) minBedrooms = 0;
      else {
        const match = lead.apartment_size.match(/(\d+)/);
        if (match) minBedrooms = parseInt(match[1]);
      }
    }

    // PKT-0267: conversion is intentionally a canonical, self-owned action.
    // The former editable conversion dialog could not atomically persist its
    // draft fields and assignee, so exposing it was deceptive. Operators must
    // update the lead source record first; this action converts that exact row.
    void budget; void minBedrooms; void locationParts; void pets;
    if (!firstName || !(lead.email || "").trim()) {
      setSnackbar({
        open: true,
        message: "Update the lead's saved first name and email before conversion",
        severity: "error",
      });
      return;
    }
    try {
      const result = await runLeadAction(lead.id, "convert");
      await Promise.all([loadClients(), loadLeads()]);
      setSnackbar({
        open: true,
        message: `${displayName} converted to your clients; shortlist preparation task ${result.replayed ? "confirmed" : "created"}.`,
        severity: "success",
      });
    } catch (error) {
      // EVENT-2179 · this is the CLAIM route, not the client save path. Routing it
      // through `getClientSaveErrorMessage` mislabelled a claim refusal as a SAVE
      // failure and leaked the status code with it: that function tests `error.code`,
      // which `ApiError` does not carry (it has `status`/`body`), so a refused claim
      // rendered "Failed to save client: API error: 409".
      setSnackbar({
        open: true,
        message: leadActionErrorMessage(error, displayName),
        severity: "error",
      });
    }
  };

  const handleConfirmConvert = async () => {
    if (!convertingLead) return;
    try {
      // Migration 049 compiles identity and preferences from the canonical
      // lead row. The dialog may preview those fields, but cannot create a
      // second client payload outside the transaction.
      if (!convertFormData.first_name || !convertFormData.email) {
        setSnackbar({
          open: true,
          message: "This lead needs a saved first name and email before conversion",
          severity: "error",
        });
        return;
      }
      const result = await runLeadAction(convertingLead.id, "convert");

      loadClients();
      loadLeads();
      setConvertDialogOpen(false);
      setConvertingLead(null);
      setSnackbar({
        open: true,
        message: `${[convertFormData.first_name, convertFormData.last_name]
          .filter(Boolean)
          .join(" ")
          .trim()} converted; shortlist preparation task ${result.replayed ? "confirmed" : "created"}.`,
        severity: "success",
      });
    } catch (error: unknown) {
      console.error("Error converting lead:", error);
      // EVENT-2179 · claim route, not the save path — see the sibling handler above.
      setSnackbar({
        open: true,
        message: leadActionErrorMessage(
          error,
          [convertFormData.first_name, convertFormData.last_name]
            .filter(Boolean)
            .join(" ")
            .trim() || "This lead",
        ),
        severity: "error",
      });
    }
  };

  return (
    <Box sx={{ pb: 4 }}>
      <LoadingTopBar loading={refreshingClients} />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 4,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <PageHeaderTitle
          icon={<GroupIcon />}
          title={isAgentRole ? "My Clients" : "Leads"}
          titleVariant="h5"
        />
        {!isMobile && (
          <Stack direction="row" spacing={2}>
            <PageRefreshButton iconOnly
              loading={refreshingClients}
              onClick={async () => {
                await Promise.all(
                  isAgentRole ? [loadClients()] : [loadClients(), loadLeads()]
                );
              }}
            />
            {!isDemo && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleNewClient}
              >
                Add Client
              </Button>
            )}
          </Stack>
        )}
      </Box>

      {/* Tabs (replaces the prior 4-card stats grid · same counts now live as
          tab labels · Touring is its own tab that maps to the Active tab with
          statusFilter=touring · Grant directive 2026-05-16 "the 4 card buttons
          uptop are kinda redundant - should we just add touring to the section
          below and remove those card buttons"). Mobile already only showed
          horizontal-scroll cards · this aligns the two breakpoints around the
          single tab row. */}
      {/* Tabs + per-tab filter row read as a single visual surface · the
          gap between them was unnecessary whitespace (Grant directive
          2026-05-16). Zero margin-bottom on the tabs Paper · each per-tab
          filter Paper renders with mt: 0 to butt directly against it ·
          rounded corners only on the outside edges so they read as one card.
          The per-tab filter LOGIC stays untouched · search + status + agent +
          move-in filters still apply only to the active tab. */}
      {/* Ch78 · who is working what — managers/admins, desktop. Tap a locator
          to see their clients on the Active tab; "Unassigned" is the gap. */}
      {!isAgentRole && !isMobile && !showRebuiltLeadsPage && (
        <LocatorWorkloadStrip
          clients={clients}
          agents={agents}
          selectedAgentId={mainTab === "active" && agentFilter !== "all" ? agentFilter : null}
          onPickAgent={(agentId) => {
            setMainTab("active");
            setAgentFilter(agentId);
          }}
        />
      )}

      <Paper
        sx={{
          mb: 0,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        }}
      >
        <Tabs
          // Derived selected value · "touring" surfaces when the operator has
          // both active-tab AND touring-filter active. Any other active state
          // shows the plain Active Clients tab.
          value={
            mainTab === "active" && statusFilter === "touring"
              ? "touring"
              : mainTab
          }
          onChange={(_, v) => {
            if (v === "touring") {
              setMainTab("active");
              setStatusFilter("touring");
            } else if (v === "active") {
              setMainTab("active");
              // Clear touring filter when explicitly clicking Active Clients
              // so it shows all active rows (not just the touring subset).
              if (statusFilter === "touring") setStatusFilter("all");
            } else {
              setMainTab(v as MainTab);
            }
          }}
          variant={isMobile ? "fullWidth" : "standard"}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              minWidth: 0,
              ...(isMobile && {
                fontSize: "0.8rem",
                px: 1,
                whiteSpace: "nowrap",
              }),
            },
          }}
        >
          {!isAgentRole && (
            <Tab
              value="leads"
              label={
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={0.75}
                sx={{ whiteSpace: "nowrap", width: "100%" }}
              >
                <span>{isMobile ? "Leads" : "Incoming Leads"}</span>
                {!isMobile && !leadsInitialLoading && newLeadsCount > 0 && (
                  <Chip
                    size="small"
                    label={newLeadsCount}
                    color="warning"
                    sx={{
                      height: 20,
                      minWidth: 24,
                      "& .MuiChip-label": { px: 1 },
                    }}
                  />
                )}
              </Box>
              }
            />
          )}
          <Tab
            value="active"
            label={
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={0.75}
                sx={{ whiteSpace: "nowrap", width: "100%" }}
              >
                <span>
                  {clientsInitialLoading ? "Pipeline" : `Pipeline (${stats.total})`}
                </span>
              </Box>
            }
          />
          <Tab
            value="touring"
            label={
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={0.75}
                sx={{ whiteSpace: "nowrap", width: "100%" }}
              >
                <span>
                  {isMobile
                    ? "Touring"
                    : clientsInitialLoading
                    ? "Touring"
                    : `Touring (${stats.touring})`}
                </span>
              </Box>
            }
          />
          <Tab
            value="out_of_contact"
            label={
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={0.75}
                sx={{ whiteSpace: "nowrap", width: "100%" }}
              >
                <span>
                  {isMobile
                    ? "Inactive"
                    : clientsInitialLoading
                    ? "Archive"
                    : `Archive (${stats.outOfContact})`}
                </span>
              </Box>
            }
          />
        </Tabs>
      </Paper>

      {/* Incoming Leads Tab */}
      {!isAgentRole && mainTab === "leads" && (
        <>
          {leadsLoading ? (
            <PageLoadingScaffold loading variant="list" rows={6} showTopBar={false} />
          ) : (
              <LeadsPage
                leads={leads}
                clients={clients}
                agents={agents}
                claims={leadClaims}
                claimsError={leadClaims === null}
                onOpenLead={(lead) => setTimelineLead(lead)}
                onOpenClient={(client) => setBoardClientId(client.id)}
                onLeadsChanged={() => {
                  void Promise.all([loadClients(), loadLeads()]);
                }}
                // Assigning a lead to somebody ELSE is an admin override in
                // routes/lead_conversion.py; anyone else may only claim to
                // themselves. The button is gated here so the UI cannot offer
                // an action the server will refuse.
                canAssign={(currentUser?.role || "").toLowerCase() === "admin"}
                // The signature on a drafted opener. currentUser here is
                // only {id, role}; the agent roster is where names live.
                senderName={agents.find((a) => a.id === currentUser?.id)?.full_name || null}
                senderId={currentUser?.id ?? null}
                senderRole={currentUser?.role ?? null}
              />
          )}
        </>
      )}

      {/* Active Clients Tab */}
      {mainTab === "active" && (
        <>
          <Paper
            sx={{
              p: 2,
              mb: 3,
              mt: 0,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              borderTop: "1px solid rgba(148, 163, 184, 0.12)",
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  placeholder="Search clients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearchQuery("")}
                        >
                          <CloseIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              {!isMobile && (
                <Grid item xs={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      label="Status"
                      onChange={(e) =>
                        setStatusFilter(e.target.value as ClientNavigationStatus | "all")
                      }
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="invoice_sent">Invoice Sent</MenuItem>
                      {pipelineStatuses.map((status) => (
                        <MenuItem key={status} value={status}>
                          <Box display="flex" alignItems="center" gap={0.75} sx={{ whiteSpace: "nowrap" }}>
                            <CircleIcon
                              sx={{
                                fontSize: 10,
                                color: statusConfig[status].color,
                              }}
                            />
                            {statusConfig[status].label}
                          </Box>
                        </MenuItem>
                      ))}
                      <MenuItem value="closed">
                        <Box display="flex" alignItems="center" gap={0.75} sx={{ whiteSpace: "nowrap" }}>
                          <CircleIcon
                            sx={{
                              fontSize: 10,
                              color: statusConfig.closed.color,
                            }}
                          />
                          Closed
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {isMobile && canManageAssignments && (
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Agent</InputLabel>
                    <Select
                      value={agentFilter}
                      label="Agent"
                      onChange={(e) => setAgentFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Agents</MenuItem>
                      {agents.length === 0 ? (
                        <MenuItem disabled>
                          <em>No agents found</em>
                        </MenuItem>
                      ) : (
                        agents.map((agent) => (
                          <MenuItem key={agent.id} value={agent.id}>
                            {agent.full_name || agent.email || "Unknown"}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Move-in date window — Davis 2026-04-25 */}
              {isMobile && (
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Move-in</InputLabel>
                    <Select
                      value={moveInWindow}
                      label="Move-in"
                      onChange={(e) =>
                        setMoveInWindow(
                          e.target.value as "all" | "30" | "60" | "90"
                        )
                      }
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="30">Within 30 days</MenuItem>
                      <MenuItem value="60">Within 60 days</MenuItem>
                      <MenuItem value="90">Within 90 days</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {isMobile && (
                <Grid
                  item
                  xs={canManageAssignments ? 6 : 12}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "stretch",
                  }}
                >
                  <ToggleButtonGroup
                    value={viewMode === "pipeline" ? "grid" : viewMode}
                    exclusive
                    onChange={(_, mode) => mode && setViewMode(mode)}
                    size="small"
                    sx={{ width: "100%", "& .MuiToggleButton-root": { flex: 1 } }}
                  >
                    <ToggleButton value="grid" aria-label="Grid view">
                      <ViewModuleIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton value="list" aria-label="List view">
                      <ViewListIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton value="needs_touch" aria-label="Needs a touch">
                      <AccessTimeIcon fontSize="small" />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              )}

              {isMobile && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.75,
                      overflowX: "auto",
                      pb: 0.5,
                      "&::-webkit-scrollbar": { display: "none" },
                      scrollbarWidth: "none",
                    }}
                  >
                    {(["new_client", "touring", "applied"] as ClientStatus[]).map(
                      (status) => {
                        const selected = statusFilter === status;
                        const cfg = statusConfig[status];
                        return (
                          <Chip
                            key={status}
                            label={cfg.label}
                            size="small"
                            clickable
                            onClick={() =>
                              setStatusFilter(selected ? "all" : status)
                            }
                            sx={{
                              flex: 1,
                              borderRadius: 1.5,
                              fontWeight: 500,
                              minWidth: 0,
                              borderColor: selected
                                ? cfg.color
                                : alpha(cfg.color, 0.4),
                              borderWidth: 1.5,
                              borderStyle: "solid",
                              backgroundColor: selected
                                ? alpha(cfg.color, 0.18)
                                : "transparent",
                              color: selected ? cfg.color : "text.secondary",
                              "&:hover": {
                                backgroundColor: alpha(cfg.color, 0.1),
                              },
                            }}
                          />
                        );
                      }
                    )}
                  </Box>
                </Grid>
              )}

              {!isMobile && canManageAssignments && (
                <Grid item xs={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Agent</InputLabel>
                    <Select
                      value={agentFilter}
                      label="Agent"
                      onChange={(e) => setAgentFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Agents</MenuItem>
                      {agents.length === 0 ? (
                        <MenuItem disabled>
                          <em>No agents found</em>
                        </MenuItem>
                      ) : (
                        agents.map((agent) => (
                          <MenuItem key={agent.id} value={agent.id}>
                            {agent.full_name || agent.email || "Unknown"}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Move-in date window — Davis 2026-04-25 */}
              {!isMobile && (
                <Grid item xs={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Move-in</InputLabel>
                    <Select
                      value={moveInWindow}
                      label="Move-in"
                      onChange={(e) =>
                        setMoveInWindow(
                          e.target.value as "all" | "30" | "60" | "90"
                        )
                      }
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="30">Within 30 days</MenuItem>
                      <MenuItem value="60">Within 60 days</MenuItem>
                      <MenuItem value="90">Within 90 days</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {!isMobile && (
                <Grid
                  item
                  xs={12}
                  md="auto"
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    ml: { md: "auto" },
                  }}
                >
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <ToggleButtonGroup
                      value={viewMode}
                      exclusive
                      onChange={(_, mode) => mode && setViewMode(mode)}
                      size="small"
                    >
                      <ToggleButton value="grid">
                        <Tooltip title="Grid View">
                          <ViewModuleIcon />
                        </Tooltip>
                      </ToggleButton>
                      <ToggleButton value="list">
                        <Tooltip title="List View">
                          <ViewListIcon />
                        </Tooltip>
                      </ToggleButton>
                      <ToggleButton value="pipeline">
                        <Tooltip title="Pipeline View">
                          <ViewKanbanIcon />
                        </Tooltip>
                      </ToggleButton>
                      <ToggleButton value="needs_touch" aria-label="Needs a touch">
                        <Tooltip title="Needs a touch">
                          <AccessTimeIcon />
                        </Tooltip>
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                </Grid>
              )}
            </Grid>
          </Paper>

          {loading ? (
            <PageLoadingScaffold loading variant="card-grid" rows={6} showTopBar={false} />
          ) : statusFilter === "invoice_sent" && invoiceSentClientIds == null ? (
            <Alert severity={invoiceSentClientIds === undefined ? "info" : "warning"}>
              {invoiceSentClientIds === undefined
                ? "Checking Invoice Sent…"
                : "Could not check Invoice Sent. Matching clients are unknown. Use Refresh to try again."}
            </Alert>
          ) : viewMode === "needs_touch" ? (
            <NeedsTouchView
              clients={filteredActiveClients}
              onViewDetails={handleViewDetails}
              invoicedClientIds={invoicedClientIds}
            />
          ) : viewMode === "pipeline" ? (
            <PipelineView
              clients={filteredActiveClients}
              onViewDetails={handleViewDetails}
              onStatusChange={handleStatusChange}
            />
          ) : viewMode === "grid" ? (
            <Grid container spacing={3}>
              {filteredActiveClients.map((client) => (
                <Grid item xs={12} sm={6} lg={4} key={client.id}>
                  <ClientCard
                    client={client}
                    onEdit={handleEditClient}
                    onDelete={handleDeleteClient}
                    onViewDetails={handleViewDetails}
                    onQuickAction={handleQuickAction}
                    onStatusChange={handleStatusChange}
                    smsUnreachable={smsUnreachableIds.has(client.id)}
                  />
                </Grid>
              ))}
            </Grid>
          ) : isMobile ? (
            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              {filteredActiveClients.map((client, idx, arr) => {
                const cfg = statusConfig[client.status];
                const lastContact = client.last_contacted_at
                  ? formatRelativeTime(client.last_contacted_at)
                  : "Never contacted";
                const captionBits = [lastContact];
                if (client.assigned_agent?.full_name) {
                  captionBits.push(client.assigned_agent.full_name);
                }
                return (
                  <Box
                    key={client.id}
                    onClick={() => handleViewDetails(client)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      px: 1.5,
                      py: 1.25,
                      cursor: "pointer",
                      borderBottom:
                        idx < arr.length - 1
                          ? `1px solid ${alpha(theme.palette.divider, 0.6)}`
                          : "none",
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        background: gradients.primary,
                        width: 36,
                        height: 36,
                        fontSize: "0.85rem",
                      }}
                    >
                      {client.first_name[0]}
                      {client.last_name[0]}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {client.first_name} {client.last_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {captionBits.join(" \u00b7 ")}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={cfg.label}
                      sx={{
                        flexShrink: 0,
                        height: 22,
                        backgroundColor: alpha(cfg.color, 0.15),
                        color: cfg.color,
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                );
              })}
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Client</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Agent</TableCell>
                    <TableCell>Last Contact</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredActiveClients.map((client) => (
                    <TableRow
                      key={client.id}
                      hover
                      onClick={() => handleViewDetails(client)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {client.first_name} {client.last_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{client.email}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {client.phone}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={statusConfig[client.status].label}
                          sx={{
                            backgroundColor: alpha(
                              statusConfig[client.status].color,
                              0.15
                            ),
                            color: statusConfig[client.status].color,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {client.assigned_agent?.full_name || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {client.last_contacted_at
                            ? formatRelativeTime(client.last_contacted_at)
                            : "Never"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="flex-end"
                        >
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleQuickAction("call", client);
                            }}
                          >
                            <PhoneIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleQuickAction("email", client);
                            }}
                          >
                            <EmailIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!loading && !(statusFilter === "invoice_sent" && invoiceSentClientIds == null) && filteredActiveClients.length === 0 && (
            <Paper sx={{ p: 8, textAlign: "center" }}>
              <PersonIcon
                sx={{
                  fontSize: 80,
                  color: "text.secondary",
                  opacity: 0.3,
                  mb: 2,
                }}
              />
              <Typography variant="h5" color="text.secondary" gutterBottom>
                {searchQuery ? "No clients found" : "No active clients yet"}
              </Typography>
              {!isDemo && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleNewClient}
                >
                  Add Client
                </Button>
              )}
            </Paper>
          )}
        </>
      )}

      {/* Archive Tab */}
      {mainTab === "out_of_contact" && (
        <>
          <Paper
            sx={{
              p: 2,
              mb: 3,
              mt: 0,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              borderTop: "1px solid rgba(148, 163, 184, 0.12)",
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Search archived clients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Paper>

          {loading ? (
            <CircularProgress />
          ) : filteredOutOfContactClients.length === 0 ? (
            <Paper sx={{ p: 8, textAlign: "center" }}>
              <PhoneDisabledIcon
                sx={{
                  fontSize: 80,
                  color: "text.secondary",
                  opacity: 0.3,
                  mb: 2,
                }}
              />
              <Typography variant="h5" color="text.secondary" gutterBottom>
                No archived clients
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Clients you've lost contact with will appear here
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {filteredOutOfContactClients.map((client) => (
                <Grid item xs={12} sm={6} lg={4} key={client.id}>
                  <Card
                    onClick={() => handleViewDetails(client)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleViewDetails(client);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    sx={{
                      opacity: 0.8,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: 4,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: 4,
                        background: statusConfig.out_of_contact.color,
                      }}
                    />
                    <CardContent>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        mb={2}
                      >
                        <Box display="flex" gap={2}>
                          <Avatar sx={{ background: gradients.primary }}>
                            {client.first_name[0]}
                            {client.last_name[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="h6" fontWeight={600}>
                              {client.first_name} {client.last_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {client.email}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mb={2}
                      >
                        Last contact:{" "}
                        {client.last_contacted_at
                          ? formatRelativeTime(client.last_contacted_at)
                          : "Never"}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<UnarchiveIcon />}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleQuickAction("restoreClient", client);
                          }}
                        >
                          Restore
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {/* Call Dialog (desktop) */}
      <Dialog
        open={callDialogOpen}
        onClose={() => {
          setCallDialogOpen(false);
          setCallDialogClient(null);
          setCallPhoneCopied(false);
        }}
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1, pr: 7, position: "relative" }}><span style={{ position: "absolute", right: 8, top: 8 }}><DesignTooltip title="Close"><span><DesignIconButton aria-label="Close" onClick={() => { setCallDialogOpen(false); setCallDialogClient(null); setCallPhoneCopied(false); }}  sx={{ width: 40, height: 40 }}><DesignClose sx={{ fontSize: 20 }} /></DesignIconButton></span></DesignTooltip></span>
          Call {callDialogClient?.first_name} {callDialogClient?.last_name}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {callDialogClient?.phone ? (
            <Box>
              <Typography
                variant="h5"
                fontWeight={600}
                sx={{ textAlign: "center", py: 2, fontFamily: "monospace" }}
              >
                {formatPhoneNumber(callDialogClient.phone)}
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center">
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<PhoneIcon />}
                  href={`tel:${callDialogClient.phone.replace(/[^\d+]/g, "")}`}
                  onClick={() => {
                    setCallDialogOpen(false);
                    setLogActivityClient(callDialogClient);
                    setLogActivityInitialType("call_logged");
                    setLogActivityDialogOpen(true);
                  }}
                >
                  Call
                </Button>
                <Button
                  variant="outlined"
                  startIcon={callPhoneCopied ? <CheckIcon /> : <ContentCopyIcon />}
                  onClick={() => {
                    if (callDialogClient?.phone) {
                      navigator.clipboard.writeText(callDialogClient.phone);
                      setCallPhoneCopied(true);
                      setTimeout(() => setCallPhoneCopied(false), 1500);
                    }
                  }}
                >
                  {callPhoneCopied ? "Copied" : "Copy"}
                </Button>
              </Stack>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No phone number on file.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              const c = callDialogClient;
              setCallDialogOpen(false);
              if (c) {
                setLogActivityClient(c);
                setLogActivityInitialType("call_logged");
                setLogActivityDialogOpen(true);
              }
            }}
          >
            Log Call
          </Button>

        </DialogActions>
      </Dialog>

      {/* Detail Drawer */}
      <DetailDrawer
        key={urlOwnedDetailClient?.id ?? "no-client"}
        client={urlOwnedDetailClient}
        open={detailDrawerOpen && Boolean(urlOwnedDetailClient)}
        onClose={() => {
          setDetailDrawerOpen(false);
          setDrawerInitialTab(null);
          setDrawerOpenAction(null);
          onClientDrawerClose?.();
        }}
        onEdit={handleEditClient}
        onQuickAction={handleQuickAction}
        onStatusChange={handleStatusChange}
        onRefresh={loadClients}
        agents={agents}
        currentUserRole={currentUser?.role || "agent"}
        initialTab={initialDrawerTab || drawerInitialTab || undefined}
        onTabChange={handleDrawerTabChange}
        openAction={drawerOpenAction}
      />

      {markPaid.dialog}

      {/* Log Activity Dialog */}
      <LogActivityDialog
        open={logActivityDialogOpen}
        onClose={() => {
          setLogActivityDialogOpen(false);
          setLogActivityClient(null);
          setLogActivityInitialType(undefined);
          setLogActivityExtraMetadata(undefined);
        }}
        client={logActivityClient}
        initialActivityType={logActivityInitialType}
        extraMetadata={logActivityExtraMetadata}
        onSuccess={handleActivityLogSuccess}
      />

      {/* Edit/Create Client Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1.5, pr: 7, position: "relative" }}><span style={{ position: "absolute", right: 8, top: 8 }}><DesignTooltip title="Close"><span><DesignIconButton aria-label="Close" onClick={() => setEditDialogOpen(false)}  sx={{ width: 40, height: 40 }}><DesignClose sx={{ fontSize: 20 }} /></DesignIconButton></span></DesignTooltip></span>
          <Box display="flex" alignItems="center" gap={1}>
            {editingClient ? (
              <PersonIcon color="primary" />
            ) : (
              <PersonAddIcon color="primary" />
            )}
            <span>{editingClient ? "Edit Client" : "Add New Client"}</span>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ overflow: "hidden" }}>
          <Grid container spacing={1.5} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="First Name"
                value={formData.first_name || ""}
                onChange={(e) =>
                  setFormData({ ...formData, first_name: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Last Name"
                value={formData.last_name || ""}
                onChange={(e) =>
                  setFormData({ ...formData, last_name: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Email"
                type="email"
                value={formData.email || ""}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Phone"
                value={formData.phone || ""}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Max Budget"
                type="number"
                value={formData.max_budget || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    max_budget: Number(e.target.value),
                  })
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Min Bedrooms"
                type="number"
                value={formData.min_bedrooms || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    min_bedrooms: Number(e.target.value),
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Move-in Date"
                type="date"
                value={normalizeDateInputValue(clientMoveIn(formData))}
                helperText={!formData.move_in_date && formData.move_in_timeline && !normalizeDateInputValue(formData.move_in_timeline) ? formData.move_in_timeline : undefined}
                onChange={(e) =>
                  setFormData({ ...formData, move_in_date: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            {canManageAssignments && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Assigned Agent</InputLabel>
                  <Select
                    value={formData.assigned_agent_id || ""}
                    label="Assigned Agent"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        assigned_agent_id: e.target.value,
                      })
                    }
                  >
                    <MenuItem value="">
                      <em>Unassigned</em>
                    </MenuItem>
                    {agents.length === 0 ? (
                      <MenuItem disabled>
                        <em>No agents found</em>
                      </MenuItem>
                    ) : (
                      agents.map((agent) => (
                        <MenuItem key={agent.id} value={agent.id}>
                          <Box display="flex" alignItems="center" gap={0.75} sx={{ whiteSpace: "nowrap" }}>
                            <Avatar
                              src={agent.avatar_url}
                              sx={{ width: 24, height: 24, fontSize: "0.75rem" }}
                            >
                              {agent.full_name?.[0]}
                            </Avatar>
                            {agent.full_name || agent.email || "Unknown"}
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <Divider sx={{ my: 0.5, opacity: 0.5 }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status || "new_client"}
                  label="Status"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      status: e.target.value as ClientStatus,
                    })
                  }
                >
                  {Object.entries(statusConfig).map(([key, { label }]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.client_type || "renter"}
                  label="Type"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      client_type: e.target.value as ClientType,
                    })
                  }
                >
                  {Object.entries(typeConfig).map(([key, { label }]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority || "normal"}
                  label="Priority"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      priority: e.target.value as ClientPriority,
                    })
                  }
                >
                  {Object.entries(priorityConfig).map(([key, { label }]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Notes From Client"
                multiline
                rows={6}
                value={formData.notes || ""}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveClient} variant="contained">
            {editingClient ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert Lead Dialog — modernized: full-screen sheet on mobile,
          grouped sections, lead avatar header, sticky footer. Same fields,
          state (convertFormData) and handlers (handleConfirmConvert). */}
      <Dialog
        open={convertDialogOpen}
        onClose={() => setConvertDialogOpen(false)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
            borderRadius: isMobile ? 0 : 3,
            ...(isMobile ? {} : { maxWidth: 540 }),
          },
        }}
      >
        {/* Header: lead avatar + name + email */}
        <Box
          sx={{
            position: "relative",
            flexShrink: 0,
            px: { xs: 2.5, sm: 3 },
            pt: { xs: "calc(env(safe-area-inset-top, 0px) + 18px)", sm: 2.5 },
            pb: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <IconButton
            onClick={() => setConvertDialogOpen(false)}
            aria-label="Close"
            sx={{
              position: "absolute",
              top: { xs: "calc(env(safe-area-inset-top, 0px) + 8px)", sm: 8 },
              right: 8,
              color: "text.secondary",
            }}
          >
            <CloseIcon />
          </IconButton>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pr: 4 }}>
            <Avatar
              sx={{
                width: 46,
                height: 46,
                fontWeight: 800,
                bgcolor: alpha(theme.palette.primary.main, 0.16),
                color: "primary.main",
              }}
            >
              {((convertFormData.first_name?.[0] || "") +
                (convertFormData.last_name?.[0] || "")).toUpperCase() || "?"}
            </Avatar>
            <Box minWidth={0}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <SwapHorizIcon sx={{ fontSize: 15, color: "primary.main" }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: ".08em",
                    textTransform: "uppercase",
                    color: "primary.main",
                  }}
                >
                  Convert lead to client
                </Typography>
              </Stack>
              <Typography
                variant="h6"
                fontWeight={800}
                noWrap
                sx={{ lineHeight: 1.2 }}
              >
                {`${convertFormData.first_name || ""} ${
                  convertFormData.last_name || ""
                }`.trim() || "New client"}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {convertFormData.email ||
                  convertFormData.phone ||
                  "Review the details below"}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Body: grouped sections, scrollable between header + footer */}
        <DialogContent
          sx={{
            flex: 1,
            overflowY: "auto",
            px: { xs: 2.5, sm: 3 },
            py: 2.5,
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          <Box>
            <Typography sx={sectionLabelSx}>Contact</Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First name"
                  value={convertFormData.first_name || ""}
                  onChange={(e) =>
                    setConvertFormData({
                      ...convertFormData,
                      first_name: e.target.value,
                    })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last name (optional)"
                  value={convertFormData.last_name || ""}
                  onChange={(e) =>
                    setConvertFormData({
                      ...convertFormData,
                      last_name: e.target.value,
                    })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={convertFormData.email || ""}
                  onChange={(e) =>
                    setConvertFormData({
                      ...convertFormData,
                      email: e.target.value,
                    })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={convertFormData.phone || ""}
                  onChange={(e) =>
                    setConvertFormData({
                      ...convertFormData,
                      phone: e.target.value,
                    })
                  }
                />
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography sx={sectionLabelSx}>Search criteria</Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Max budget"
                  type="number"
                  value={convertFormData.max_budget || ""}
                  onChange={(e) =>
                    setConvertFormData({
                      ...convertFormData,
                      max_budget: Number(e.target.value),
                    })
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Min bedrooms"
                  type="number"
                  value={convertFormData.min_bedrooms || ""}
                  onChange={(e) =>
                    setConvertFormData({
                      ...convertFormData,
                      min_bedrooms: Number(e.target.value),
                    })
                  }
                />
              </Grid>
            </Grid>
            {convertingLead &&
              (() => {
                const locationParts = buildLeadLocationParts(
                  convertingLead.neighborhoods,
                  convertingLead.neighborhood_other,
                );
                const pets = formatLeadPets(convertingLead.pets);
                if (locationParts.length === 0 && !pets) return null;
                return (
                  <Paper
                    variant="outlined"
                    sx={{
                      mt: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      borderColor: alpha(theme.palette.primary.main, 0.18),
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mb={0.75}
                    >
                      From the lead
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {locationParts.map((location) => (
                        <Chip
                          key={location}
                          size="small"
                          label={location}
                          variant="outlined"
                        />
                      ))}
                      {pets && (
                        <Chip
                          size="small"
                          label={`Pets: ${pets}`}
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mt={0.75}
                    >
                      Neighborhoods save as preferred areas; pets are preserved
                      in notes.
                    </Typography>
                  </Paper>
                );
              })()}
          </Box>

          <Box>
            <Typography sx={sectionLabelSx}>Notes from client</Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={8}
              placeholder="Anything the client told you…"
              value={convertFormData.notes || ""}
              onChange={(e) =>
                setConvertFormData({
                  ...convertFormData,
                  notes: e.target.value,
                })
              }
            />
          </Box>

          {canManageAssignments && (
            <Box>
              <Typography sx={sectionLabelSx}>Assign to</Typography>
              <FormControl fullWidth>
                <InputLabel>Agent</InputLabel>
                <Select
                  value={convertFormData.assigned_agent_id || ""}
                  label="Agent"
                  onChange={(e) =>
                    setConvertFormData({
                      ...convertFormData,
                      assigned_agent_id: e.target.value,
                    })
                  }
                >
                  <MenuItem value="">
                    <em>Unassigned</em>
                  </MenuItem>
                  {agents.length === 0 ? (
                    <MenuItem disabled>
                      <em>No agents found</em>
                    </MenuItem>
                  ) : (
                    agents.map((agent) => (
                      <MenuItem key={agent.id} value={agent.id}>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={0.75}
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          <Avatar
                            src={agent.avatar_url}
                            sx={{ width: 24, height: 24, fontSize: "0.75rem" }}
                          >
                            {agent.full_name?.[0]}
                          </Avatar>
                          {agent.full_name || agent.email || "Unknown"}
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>

        {/* Sticky footer */}
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 2.5, sm: 3 },
            py: 2,
            pb: { xs: "calc(env(safe-area-inset-bottom, 0px) + 14px)", sm: 2 },
            borderTop: `1px solid ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1.5,
            bgcolor: "background.paper",
          }}
        >
          <Button
            onClick={() => setConvertDialogOpen(false)}
            sx={{
              color: "text.secondary",
              fontWeight: 700,
              flex: "0 0 auto",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmConvert}
            variant="contained"
            size="large"
            startIcon={<PersonAddIcon />}
            sx={{
              fontWeight: 800,
              borderRadius: 2,
              px: 3,
              whiteSpace: "nowrap",
              flex: { xs: 1, sm: "0 0 auto" },
            }}
          >
            Convert to Client
          </Button>
        </Box>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Guest Card Dialog */}
      <GuestCardDialog
        open={guestCardDialogOpen}
        onClose={() => setGuestCardDialogOpen(false)}
        onNavigateToSettings={() =>
          onNavigate?.("settings", undefined, { settingsTab: "integrations" })
        }
        client={guestCardClient as ClientInfo | null}
        isDemo={isDemo}
        onSuccess={() => {
          loadClients();
          setSnackbar({
            open: true,
            message: "Guest card sent successfully!",
            severity: "success",
          });
        }}
      />

      {/* Demo Invoice Disabled Dialog */}
      <Dialog
        open={demoInvoiceDialogOpen}
        onClose={() => setDemoInvoiceDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Invoicing</DialogTitle>
        <DialogContent>
          <Alert severity="info">
            Invoice generation is disabled in demo mode. In production, invoices
            are automatically generated as PDFs and emailed to your client.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDemoInvoiceDialogOpen(false)}>Got it</Button>
        </DialogActions>
      </Dialog>

      {/* Send Invoice Modal */}
      {sendInvoiceClient && (
        <SendInvoiceModal
          open={sendInvoiceDialogOpen}
          onClose={() => {
            setSendInvoiceDialogOpen(false);
            setSendInvoiceClient(null);
          }}
          client={sendInvoiceClient}
          onSuccess={handleInvoiceSuccess}
          currentAgent={agents.find(
            (a) => a.id === sendInvoiceClient.assigned_agent_id
          )}
        />
      )}

      {/* Upload Lease Modal */}
      {uploadLeaseClient && (
        <UploadLeaseModal
          open={uploadLeaseDialogOpen}
          onClose={() => {
            setUploadLeaseDialogOpen(false);
            setUploadLeaseClient(null);
          }}
          client={uploadLeaseClient}
          onSuccess={() => {
            loadClients();
            setSnackbar({
              open: true,
              message: "Lease emailed successfully",
              severity: "success",
            });
          }}
          currentAgent={
            agents.find(
              (a) => a.id === uploadLeaseClient.assigned_agent_id
            ) || null
          }
          isDemo={isDemo}
        />
      )}

      {/* Ch78 · a lead opened from the move-in timeline: the dashboard's own
          detail modal, with its one action (Convert), over the Leads page. */}
      <ClientRecordDialog
        clientId={boardClientId}
        open={boardClientId !== null}
        onClose={() => setBoardClientId(null)}
      />
      <LeadDetailModal
        lead={timelineLead ? leadToDashboardRow(timelineLead) : null}
        open={!!timelineLead}
        onClose={() => setTimelineLead(null)}
        canConvert={!!currentUser && !isDemo}
        onConvert={async () => {
          if (!timelineLead) return;
          await handleConvertLead(timelineLead);
          setTimelineLead(null);
        }}
      />

      {!isDemo && isMobile && (
        <Fab
          color="primary"
          aria-label="Add Client"
          onClick={handleNewClient}
          sx={{
            position: "fixed",
            // Clear the floating bottom nav pill on mobile (it occupies the
            // bottom ~74px band and sits above the Fab's z-index).
            bottom: {
              xs: "calc(env(safe-area-inset-bottom, 0px) + 88px)",
              sm: 24,
            },
            right: 24,
          }}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  );
};

export default ClientList;
